// COMY1-398 — the bell is filtered by the context the user is in: the role
// view (community manager / supplier) plus the managed community or the
// supplier's business. The notifications service (utils,
// sockets/notification-context.ts) applies the rules; this file only builds
// the socket payload and reads the per-context unread counts.

const MANAGEMENT_ROLE_VIEWS = ["COMMUNITY_MANAGER", "COMMUNITY_MODERATOR"];

/**
 * Socket payload for the current sidebar data, or null (= unfiltered, as
 * before) when the role view is unknown — e.g. while the sidebar loads.
 */
export function notificationContextFromSidebar(sidebarData) {
  const roleView = sidebarData?.last_role_view;
  if (MANAGEMENT_ROLE_VIEWS.includes(roleView)) {
    return sidebarData.communityId
      ? { role: "community_manager", communityId: Number(sidebarData.communityId) }
      : { role: "community_manager" };
  }
  if (roleView === "SUPPLIER") {
    return sidebarData.supplierId
      ? { role: "supplier", supplierId: Number(sidebarData.supplierId) }
      : { role: "supplier" };
  }
  return null;
}

/** Stable key so the socket only re-fetches when the context really changes. */
export function notificationContextKey(context) {
  return context
    ? `${context.role}:${context.communityId ?? ""}:${context.supplierId ?? ""}`
    : "";
}

/**
 * Unread notifications the admin app would show in ANOTHER context (another
 * managed community, or the other role view) — for the dot next to the
 * community switcher. Member notifications belong to the members app and are
 * never counted here.
 *
 * @param groups `unread_by_context` payload: [{ role, community_id, supplier_id, count, in_current_context }]
 * @param access { communityIds: string[] (managed communities, may be empty while unknown),
 *                 canManage: boolean, isSupplier: boolean }
 */
export function otherContextsUnreadCount(groups, access) {
  const communityIds = new Set((access?.communityIds ?? []).map(String));
  const managesCommunity = (communityId) =>
    communityId === null ||
    communityIds.size === 0 ||
    communityIds.has(String(communityId));

  let total = 0;
  for (const group of groups ?? []) {
    if (group.in_current_context || !(group.count > 0)) continue;
    let counts = false;
    if (group.role === "community_manager") {
      counts = Boolean(access?.canManage) && managesCommunity(group.community_id);
    } else if (group.role === "supplier") {
      counts = Boolean(access?.isSupplier);
    } else if (group.role === "all" || group.role === null) {
      counts =
        (group.community_id !== null &&
          Boolean(access?.canManage) &&
          managesCommunity(group.community_id)) ||
        (group.supplier_id !== null && Boolean(access?.isSupplier));
    }
    if (counts) total += group.count;
  }
  return total;
}
